// ─── Drop this component into AdminDashboard.jsx ──────────────────────────────
// 1. Import it at the top of AdminDashboard.jsx:
//    import WhatsAppFAQAdmin from "@/components/WhatsAppFAQAdmin";   (or wherever you place this file)
//
// 2. Add to the sidebar in AdminDashboard:
//    <Link to="/admin/whatsapp" ...><MessageCircle className="w-4 h-4 inline mr-2"/>WhatsApp FAQ</Link>
//
// 3. Add to the <Routes> inside AdminDashboard:
//    <Route path="whatsapp" element={<WhatsAppFAQAdmin />} />
//
// 4. Add MessageCircle to the lucide-react import at the top of AdminDashboard.jsx

import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Plus, Trash2, GripVertical, MessageCircle, Phone, Save } from "lucide-react";

export default function WhatsAppFAQAdmin() {
  const [number, setNumber] = useState("");
  const [questions, setQuestions] = useState([]);
  const [newQ, setNewQ] = useState("");
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/whatsapp-faq");
      const data = r.data || [];
      setNumber(data[0]?._whatsapp_number || "");
      setQuestions(data.map(d => ({ id: d.id, question: d.question })));
    } catch { toast.error("Failed to load FAQ"); }
    finally { setLoading(false); }
  };

  useEffect(() => { refresh(); }, []);

  const saveAll = async () => {
    setSaving(true);
    try {
      await api.put("/admin/whatsapp-faq", {
        whatsapp_number: number.trim(),
        questions: questions.map((q, i) => ({ id: q.id, question: q.question, order: i })),
      });
      toast.success("WhatsApp FAQ saved!");
      refresh();
    } catch (e) {
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  const addQuestion = () => {
    const t = newQ.trim();
    if (!t) return;
    setQuestions(prev => [...prev, { id: `new_${Date.now()}`, question: t }]);
    setNewQ("");
  };

  const removeQuestion = (id) => {
    setQuestions(prev => prev.filter(q => q.id !== id));
  };

  const updateQuestion = (id, value) => {
    setQuestions(prev => prev.map(q => q.id === id ? { ...q, question: value } : q));
  };

  if (loading) return <div className="text-center py-20 text-slate-400">Loading…</div>;

  return (
    <div data-testid="whatsapp-faq-admin">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-[#25D366]" />
            WhatsApp FAQ Widget
          </h2>
          <p className="text-slate-500 text-sm mt-0.5">
            Set your WhatsApp number and the pre-defined questions shown to users.
          </p>
        </div>
        <Button
          onClick={saveAll}
          disabled={saving}
          className="bg-[#25D366] hover:bg-[#20BD5A] text-white"
          data-testid="save-whatsapp-faq-btn"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? "Saving…" : "Save All"}
        </Button>
      </div>

      {/* WhatsApp Number */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
          <Phone className="w-4 h-4 text-[#25D366]" />
          WhatsApp Business Number
        </h3>
        <div className="flex gap-3 items-start max-w-md">
          <div className="flex-1 space-y-1.5">
            <Label className="text-xs text-slate-500">Phone number with country code</Label>
            <Input
              value={number}
              onChange={e => setNumber(e.target.value)}
              placeholder="e.g. 919876543210"
              data-testid="whatsapp-number-input"
            />
            <p className="text-xs text-slate-400">
              No + or spaces. Example: 919876543210 for +91-98765-43210
            </p>
          </div>
        </div>
      </div>

      {/* Pre-defined Questions */}
      <div className="bg-white border border-slate-200 rounded-xl p-5">
        <h3 className="font-semibold text-slate-800 mb-1 flex items-center gap-2">
          Pre-defined Questions
        </h3>
        <p className="text-slate-500 text-sm mb-4">
          These appear as quick-tap options in the WhatsApp chat bubble. Limit to 5–6 for best UX.
        </p>

        {/* Question List */}
        <div className="space-y-2 mb-4">
          {questions.length === 0 && (
            <div className="text-slate-400 text-sm italic py-4 text-center border border-dashed border-slate-200 rounded-xl">
              No questions yet. Add some below.
            </div>
          )}
          {questions.map((q, index) => (
            <div key={q.id} className="flex items-center gap-2 group">
              <span className="text-slate-300 cursor-grab flex-shrink-0">
                <GripVertical className="w-4 h-4" />
              </span>
              <span className="text-slate-400 text-xs w-5 flex-shrink-0 text-right">{index + 1}.</span>
              <Input
                value={q.question}
                onChange={e => updateQuestion(q.id, e.target.value)}
                className="flex-1 text-sm"
                data-testid={`faq-q-${index}`}
              />
              <Button
                size="icon"
                variant="ghost"
                onClick={() => removeQuestion(q.id)}
                className="text-red-400 hover:text-red-600 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </div>

        {/* Add new question */}
        <div className="flex gap-2">
          <Input
            value={newQ}
            onChange={e => setNewQ(e.target.value)}
            onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addQuestion(); } }}
            placeholder="Type a new question and press Enter or Add…"
            className="flex-1 text-sm"
            data-testid="new-faq-input"
          />
          <Button
            onClick={addQuestion}
            disabled={!newQ.trim()}
            variant="outline"
            className="border-[#25D366] text-[#25D366] hover:bg-[#F0FFF5]"
          >
            <Plus className="w-4 h-4 mr-1" />
            Add
          </Button>
        </div>

        {/* Preview */}
        {questions.length > 0 && (
          <div className="mt-6 border-t border-slate-100 pt-5">
            <h4 className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Preview</h4>
            <div className="bg-[#ECE5DD] rounded-xl p-3 flex flex-col gap-2 max-w-xs"
              style={{
                backgroundImage: `url("data:image/svg+xml,%3Csvg width='40' height='40' viewBox='0 0 40 40' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23d4c9bb' fill-opacity='0.4' fill-rule='evenodd'%3E%3Cpath d='M0 40L40 0H20L0 20M40 40V20L20 40'/%3E%3C/g%3E%3C/svg%3E")`,
              }}
            >
              <div className="bg-white rounded-xl px-3 py-2 shadow-sm text-sm text-slate-700 max-w-[90%]">
                👋 Hi! How can we help you today?
              </div>
              {questions.slice(0, 6).map(q => (
                <div key={q.id} className="bg-white rounded-xl px-3 py-2 shadow-sm text-sm text-slate-700 border border-slate-100 cursor-pointer hover:border-[#25D366] hover:bg-[#F0FFF5] transition-colors">
                  {q.question}
                </div>
              ))}
              <div className="bg-white rounded-xl px-3 py-2 shadow-sm text-sm text-slate-400 border border-dashed border-slate-200">
                ✏️ Other question…
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}